<script setup lang="ts">
/**
 * SG — Incorporation (Pre) Studio panel.
 * Live document package (package MVP 이식) + L_INCORPORATIONS autofill.
 */
import { ref, computed, watch, onMounted, nextTick } from 'vue';
import { useRouter } from 'vue-router';
import type { PreSetSelection } from '@/features/startTask/preset_task/shared/preset/types/preset.types';
import type { OfficerDetail } from '@/features/AI-documents/types/docGen.types';
import { getTaskConfigByCode } from '@/features/startTask/api/taskConfigApi';
import { usePresetProgress } from '@/features/startTask/preset_task/shared/preset/composables/usePresetProgress';
import { useSessionStore } from '@/shared/stores/sessionStore';
import { fetchSessionClientNm } from '@/features/startTask/preset_task/sg/composables/fetchSessionClientNm';
import { provideCddFill, bareKey, typeOf, isProgressFilled } from '../incorporation-pre/cdd/useCddFill';
import {
  provideSgPreSigners,
  SG_PRE_SIGNER_KEYS,
  buildSgPreSignersFromValues,
} from '../incorporation-pre/cdd/sgPreSignersContext';
import AuraIncorporationPackageDoc from '../incorporation-pre/AuraIncorporationPackageDoc.vue';
import {
  mapAuraIncorporationToPreValues,
  type AuraIncPreIncludes,
} from '../mappers/mapAuraIncorporationToPreValues';
import { normalizeSgPreFormDataForWord } from '../utils/normalizeSgPreFormDataForWord';
import { markAutoValues, CORP_SHAPP_BASE } from '../incorporation-pre/cdd/syncAuraDocs';
import { contactsApi } from '@/features/Contact/api/contactsApi';
import {
  loadCmBaseCodeOptions,
  nationalityLabelForOutput,
} from '@/features/startTask/preset_task/sg/composables/cmBaseCodeOptions';
import '../incorporation-pre/allotDoc.css';

const props = defineProps<{
  selection: PreSetSelection;
  entityData: Record<string, any> | null;
  taskTitle: string;
  isReady: boolean;
  currentStep: string;
  selectedPerson?: OfficerDetail | null;
  activePersonContext?: Record<string, any> | null;
  selectedAddress?: { value: string; type: string; position?: string } | null;
  activeAddressContext?: Record<string, any> | null;
  editTaskId?: number | null;
  studio?: boolean;
  pendingDocType?: string;
  appliedAddress?: {
    key: string;
    line: string;
    town: string;
    state: string;
    postcode: string;
    country: string;
    seq: number;
  } | null;
  chatFormData?: Record<string, any> | null;
}>();

const emit = defineEmits<{
  (e: 'back'): void;
  (e: 'task-created', data: { taskId: number; taskTitle: string }): void;
  (e: 'request-person-lookup', ctx: Record<string, any>): void;
  (e: 'change-entity'): void;
  (e: 'change-task-title'): void;
  (e: 'progress-change', pct: number): void;
  (e: 'request-address-input', key: string): void;
}>();

const router = useRouter();
const sessionStore = useSessionStore();
const fill = provideCddFill();
const docRef = ref<InstanceType<typeof AuraIncorporationPackageDoc> | null>(null);

const entityCd = computed(() =>
  String(
    props.entityData?.entityCd ||
      props.entityData?.icpCd ||
      props.chatFormData?.entityCd ||
      props.chatFormData?.icpCd ||
      '',
  ).trim(),
);
/** Signers 는 L_ENTITY 가 없으므로 entityCd 를 넘기지 않음 (AUTH002 방지) */
const signersCtx = provideSgPreSigners();

async function ensureCspLetterheadFilled() {
  // Setup Entity engagement.csp* 가 1순위. 비어 있을 때만 CLIENT_NM / REG_NO 폴백.
  const needName = !String(fill.values.csp_name || '').trim();
  const needUen = !String(fill.values.csp_uen || '').trim();
  if (!needName && !needUen) return;
  try {
    const customer = sessionStore.getCustomerInfo();
    if (needUen) {
      const uen = String(customer?.regNo || '').trim();
      if (uen) fill.setValue('csp_uen', uen);
    }
    if (needName) {
      const nm =
        (await fetchSessionClientNm(String(customer?.code || '').trim())) ||
        String(customer?.name || '').trim();
      if (nm) fill.setValue('csp_name', nm);
    }
  } catch (e) {
    console.warn('[SgIncorporationPre] CSP letterhead inject failed:', e);
  }
}

const taskTitleLocal = ref('');
const titleModel = computed({
  get: () => taskTitleLocal.value || props.taskTitle || '',
  set: (v: string) => {
    taskTitleLocal.value = v;
  },
});
const effectiveTitle = computed(() => titleModel.value.trim());

const studio = computed(() => !!props.studio);
const creating = ref(false);
const progress = ref('');
const presetProgress = usePresetProgress();
watch(creating, (v) => {
  presetProgress.busy = v;
  presetProgress.message = v ? progress.value || 'Creating task…' : '';
});
watch(progress, (m) => {
  if (creating.value) presetProgress.message = m;
});

const editMode = computed(() => !!props.editTaskId);
const taskConfigSteps = ref<string[]>([]);
const lastAutofillIcp = ref('');
const autofilledKeys = ref<Set<string>>(new Set());
/** 저장된 프리셋 상태를 복원했는지 — 이후 T-Link autofill 이 덮어쓰지 않게 한다(R-5) */
const restoredFromSaved = ref(false);
const pendingLocalRestore = ref<{
  form45Ids: number[];
  form45bIds: number[];
  corpCtrlIds: number[];
  indCtrlIds: number[];
  csProfileIds: number[];
  nomDirIds?: number[];
  nomShIds?: number[];
  caf: Record<string, number[]>;
} | null>(null);

/** Setup Entity Yes/No — No 인 섹션은 Pre 문서·Stage·Create Task 생성 제외 */
const sectionIncludes = ref<AuraIncPreIncludes>({
  directors: true,
  secretary: true,
  shareholders: true,
  controllers: true,
  engagement: true,
});

/** CDD 에서 인물 추가 시 Stage 문서 포함 플래그 자동 ON */
function onSyncIncludes(partial: Partial<AuraIncPreIncludes>) {
  sectionIncludes.value = {
    ...sectionIncludes.value,
    ...Object.fromEntries(
      Object.entries(partial).filter(([, v]) => v === true),
    ),
  } as AuraIncPreIncludes;
}

function emitProgress() {
  const total = fill.totalFields.value || 1;
  const filled = fill.filledCount.value;
  const pct = Math.min(100, Math.round((filled / total) * 100));
  if (pct < 100 && typeof console !== 'undefined') {
    const empty = fill.keys.value.filter((k) => !isProgressFilled(k, fill.values[k], fill.values));
    if (empty.length && empty.length <= 25) {
      console.debug('[SgIncorporationPre] progress empty keys', empty);
    }
  }
  emit('progress-change', pct);
}

watch([() => fill.filledCount.value, () => fill.totalFields.value], () => emitProgress());

fill.setActivateHandler((key: string) => {
  const bare = bareKey(key);
  const t = typeOf(key);
  if (t === 'address') {
    emit('request-address-input', key);
    return;
  }
  if (t === 'ssic') {
    emit('request-person-lookup', {
      fieldGroup: 'sg-incorporation-pre',
      key: bare,
      lookupType: 'ssic',
      scopedKey: key,
    });
    return;
  }
  if (t === 'entity') {
    // 회사명 — incorporation 링크 또는 Contact(법인) 검색
    if (
      bare === 'company_name' ||
      bare === 'entity_name' ||
      bare === 'proposed_company_name'
    ) {
      emit('request-person-lookup', {
        fieldGroup: 'sg-incorporation-pre',
        key: bare,
        lookupType: 'incorporation',
        scopedKey: key,
      });
      return;
    }
    // CSP / 법인 Controllers / Corp SH — Global Contact (법인) 검색
    emit('request-person-lookup', {
      fieldGroup: 'sg-incorporation-pre',
      key: bare,
      defaultRole: 'CONTACT',
      lookupType: 'officer',
      scopedKey: key,
      // Pre 는 L_ENTITY 가 없음 — Contact 는 client-wide
      entityCd: '',
    });
    return;
  }
  if (t === 'person') {
    // 설립 전 단계 — officer 목록 대신 Global Contact 로 조회·자동바인딩
    emit('request-person-lookup', {
      fieldGroup: 'sg-incorporation-pre',
      key: bare,
      defaultRole: 'CONTACT',
      lookupType: 'officer',
      scopedKey: key,
      entityCd: '',
    });
  }
});

fill.setAddressHandler((key: string) => {
  emit('request-address-input', key);
});

watch(
  () => props.appliedAddress,
  (a) => {
    if (!a?.key) return;
    const line = [a.line, [a.postcode, a.town].filter(Boolean).join(' '), a.state, a.country]
      .map((s) => String(s || '').trim())
      .filter(Boolean)
      .join(', ');
    if (line) fill.setValue(a.key, line);
  },
);

watch(
  () => props.selectedPerson,
  async (p) => {
    if (!p) return;
    let person: Record<string, any> = { ...(p as any) };
    // bridge 는 MY registeredAddress 위주라 SG L_GLOBAL_CONTACT 주소 컬럼이 빠질 수 있음 → getContactById 로 보강
    const contactCd = String(person.contactCd || person.CONTACT_CD || '').trim();
    if (contactCd) {
      try {
        const raw: any = await contactsApi.getContactById(contactCd);
        const c: any = raw?.contact || raw?.data?.contact || raw?.data || raw || {};
        person = {
          ...c,
          ...person,
          // UPPER_CASE 컬럼이 bridge 필드에 덮이지 않도록 주소 키는 contact 원본 유지
          BLOCK_NO: c.BLOCK_NO ?? c.blockNo ?? person.BLOCK_NO,
          STREET_NAME: c.STREET_NAME ?? c.streetName ?? person.STREET_NAME,
          BUILDING_NAME: c.BUILDING_NAME ?? c.buildingName ?? person.BUILDING_NAME,
          ADDR_LEVEL: c.ADDR_LEVEL ?? c.addrLevel ?? person.ADDR_LEVEL,
          ADDR_UNIT: c.ADDR_UNIT ?? c.addrUnit ?? person.ADDR_UNIT,
          POSTAL_CODE: c.POSTAL_CODE ?? c.postalCode ?? person.POSTAL_CODE,
          FOREIGN_ADDRESS_LINE_1:
            c.FOREIGN_ADDRESS_LINE_1 ?? c.foreignAddressLine1 ?? person.FOREIGN_ADDRESS_LINE_1,
          FOREIGN_ADDRESS_LINE_2:
            c.FOREIGN_ADDRESS_LINE_2 ?? c.foreignAddressLine2 ?? person.FOREIGN_ADDRESS_LINE_2,
          ALT_BLOCK_NO: c.ALT_BLOCK_NO ?? c.altBlockNo ?? person.ALT_BLOCK_NO,
          ALT_STREET_NAME: c.ALT_STREET_NAME ?? c.altStreetName ?? person.ALT_STREET_NAME,
          ALT_BUILDING_NAME: c.ALT_BUILDING_NAME ?? c.altBuildingName ?? person.ALT_BUILDING_NAME,
          ALT_ADDR_LEVEL: c.ALT_ADDR_LEVEL ?? c.altAddrLevel ?? person.ALT_ADDR_LEVEL,
          ALT_ADDR_UNIT: c.ALT_ADDR_UNIT ?? c.altAddrUnit ?? person.ALT_ADDR_UNIT,
          ALT_POSTAL_CODE: c.ALT_POSTAL_CODE ?? c.altPostalCode ?? person.ALT_POSTAL_CODE,
          ALT_FOREIGN_ADDRESS_LINE_1:
            c.ALT_FOREIGN_ADDRESS_LINE_1 ??
            c.altForeignAddressLine1 ??
            person.ALT_FOREIGN_ADDRESS_LINE_1,
          ALT_FOREIGN_ADDRESS_LINE_2:
            c.ALT_FOREIGN_ADDRESS_LINE_2 ??
            c.altForeignAddressLine2 ??
            person.ALT_FOREIGN_ADDRESS_LINE_2,
          primaryAddressParts: c.primaryAddressParts ?? person.primaryAddressParts,
          alternateAddressParts: c.alternateAddressParts ?? person.alternateAddressParts,
          NATIONALITY_CD:
            c.NATIONALITY_CD ?? c.nationalityCd ?? person.NATIONALITY_CD ?? person.nationalityCd,
          nationality:
            c.NATIONALITY_CD ??
            c.nationalityCd ??
            c.nationality ??
            person.NATIONALITY_CD ??
            person.nationality,
          nationalityName:
            c.nationalityName ??
            c.nationalityText ??
            c.NATIONALITY_NAME ??
            person.nationalityName ??
            person.nationalityText,
          alternateAddress:
            c.alternateAddress ?? c.serviceAddress ?? person.alternateAddress ?? person.serviceAddress,
        };
      } catch (err) {
        console.warn('[SgIncorporationPre] contact address enrich failed:', err);
      }
    }
    await fill.applyPersonToActive(person);
    emitProgress();
  },
);

/** 챗봇 SSIC 선택 → 문서 필드 반영 */
watch(
  () => props.chatFormData,
  (fd) => {
    if (!fd) return;
    const target = String(fd._ssicTargetKey || '').trim();
    if (!target) return;
    const display = String(fd._ssicDisplay || fd[target] || '').trim();
    if (!display) return;
    if (fill.values[target] !== display) {
      fill.setValue(target, display);
      emitProgress();
    }
  },
  { deep: true },
);

function flushLocalRestore() {
  const pending = pendingLocalRestore.value;
  if (!pending || !docRef.value?.restoreLocalState) return;
  docRef.value.restoreLocalState(pending);
}

function resetAutofilledValues() {
  for (const k of autofilledKeys.value) {
    fill.setValue(k, '');
  }
  // 이전 엔티티 다건 키(ubo2_* 등)가 남아 행/ % 를 오염시키지 않도록 잔여 인덱스·스코프 키도 비움
  for (const k of Object.keys(fill.values)) {
    if (
      /^(dir|sh|ubo|pep|md|ceo|pa|ot|sec|ictrl|cctrl)\d+_/.test(k) ||
      /^(f45_|f45b_|ctrlc_|ctrli_|cs_|nd_|ns_)\d+\//.test(k) ||
      /^d\d+_/.test(k) ||
      /^s\d+_/.test(k)
    ) {
      fill.setValue(k, '');
    }
  }
  autofilledKeys.value = new Set();
  lastAutofillIcp.value = '';
  sectionIncludes.value = {
    directors: true,
    secretary: true,
    shareholders: true,
    controllers: true,
    engagement: true,
  };
  pendingLocalRestore.value = {
    form45Ids: [1],
    form45bIds: [1],
    corpCtrlIds: [],
    indCtrlIds: [1],
    nomDirIds: [],
    nomShIds: [],
    csProfileIds: [1],
    caf: {
      directors: [1],
      shareholders: [1],
      ubos: [1],
      peps: [1],
      mds: [1],
      ceos: [1],
      partners: [1],
      others: [1],
    },
  };
  flushLocalRestore();
  pendingLocalRestore.value = null;
}

async function autofillFromIncorporation(icpCd: string) {
  const cd = String(icpCd || '').trim();
  if (!cd || cd === lastAutofillIcp.value) return;
  // 엔티티 전환 시 이전 autofill·다건 행을 먼저 리셋 (빈 UBO 2 잔존 / 97% 고착 방지)
  if (autofilledKeys.value.size || lastAutofillIcp.value) {
    const prevKeys = autofilledKeys.value;
    for (const k of prevKeys) fill.setValue(k, '');
    for (const k of Object.keys(fill.values)) {
      if (
        /^(dir|sh|ubo|pep|md|ceo|pa|ot|sec|ictrl|cctrl)\d+_/.test(k) ||
        /^(f45_|f45b_|ctrlc_|ctrli_|cs_|nd_|ns_)\d+\//.test(k)
      ) {
        fill.setValue(k, '');
      }
    }
    autofilledKeys.value = new Set();
  }
  lastAutofillIcp.value = cd;
  try {
    const res = await fetch(`/api/aiDocuments/incorporation/entity-data/${cd}`, {
      credentials: 'include',
    });
    const json = await res.json();
    if (!json?.success) return;
    const incData = json.data?.incorporationData || {};
    if (!incData.companyName && json.data?.companyName) {
      incData.companyName = json.data.companyName;
    }
    const mapped = mapAuraIncorporationToPreValues(incData);
    // ⚠ Edit 재진입: 저장된 프리셋 상태(restoreState)가 이미 복원돼 있으면 T-Link 원천값으로
    //   덮어쓰지 않는다. 이 fetch 는 복원보다 늦게 끝나서, 예전엔 사용자가 고친 이사명·비서·
    //   등록번호·설립일·보증금 등이 원천값으로 되돌아갔다(R-5). 빈 칸만 채우고 부수·섹션도 유지.
    const keepSaved = restoredFromSaved.value;
    if (!keepSaved) sectionIncludes.value = { ...mapped.includes };
    const written = new Set<string>();
    for (const [k, v] of Object.entries(mapped.values)) {
      if (!v) continue;
      if (keepSaved && String(fill.values[k] ?? '').trim()) continue;
      fill.setValue(k, v);
      written.add(k);
    }
    autofilledKeys.value = written;
    // T-Link 가 직접 채운 최상위 값(secretary_name 등)도 "자동값" 으로 기록 → 이후 CAF 변경을 따라감
    if (!keepSaved) markAutoValues(fill.values as Record<string, string>, written);
    if (!keepSaved) {
      pendingLocalRestore.value = {
        form45Ids: mapped.form45Ids,
        form45bIds: mapped.form45bIds,
        corpCtrlIds: mapped.corpCtrlIds,
        indCtrlIds: mapped.indCtrlIds,
        csProfileIds: mapped.csProfileIds,
        caf: mapped.caf,
      };
      await nextTick();
      flushLocalRestore();
    }
    await ensureCspLetterheadFilled();
    signersCtx.seedFromValuesIfEmpty(fill.exportPayload());
    await nextTick();
    emitProgress();
  } catch (err) {
    console.warn('[SgIncorporationPre] autofill failed:', err);
    lastAutofillIcp.value = '';
  }
}

watch(
  entityCd,
  (cd, prev) => {
    if (cd) {
      // Change entity: 이전 icp 와 다르면 강제 리셋 후 autofill
      if (prev && prev !== cd) {
        lastAutofillIcp.value = '';
        // 다른 회사로 바꿨으면 저장값 보존이 아니라 새 회사 값으로 채운다
        restoredFromSaved.value = false;
      }
      void autofillFromIncorporation(cd);
    } else if (prev) {
      resetAutofilledValues();
    }
  },
  { immediate: true },
);

/** Autofill 이 PackageDoc mount 전에 끝나면 N부 복원을 다시 적용 */
watch(docRef, (el) => {
  if (el) void nextTick(() => flushLocalRestore());
});

onMounted(async () => {
  try {
    await loadCmBaseCodeOptions('B04');
    // Autofill / 이전 세션에 남은 B04 코드 → 표시명
    for (const [k, v] of Object.entries(fill.values)) {
      if (!/(nationality|_country|country_of_incorporation)/i.test(k)) continue;
      const raw = String(v || '').trim();
      if (!/^B\d{5}$/i.test(raw)) continue;
      const label = nationalityLabelForOutput(raw);
      if (label && label !== raw) fill.setValue(k, label);
    }
  } catch {
    /* ignore */
  }
  try {
    const cfg = await getTaskConfigByCode(String(props.selection.task.TASK_CODE || '').trim());
    const steps = cfg?.processSteps;
    taskConfigSteps.value = Array.isArray(steps) ? steps : [];
  } catch {
    /* ignore */
  }
  await ensureCspLetterheadFilled();
});

function gatherState() {
  return {
    values: { ...fill.exportPayload() },
    local: docRef.value?.gatherLocalState?.() || null,
    includes: { ...sectionIncludes.value },
    signers: signersCtx.exportAll(),
    entityCd: entityCd.value,
    taskTitle: effectiveTitle.value,
  };
}

function restoreState(s: any) {
  if (!s) return;
  restoredFromSaved.value = true;
  if (s.values && typeof s.values === 'object') {
    for (const [k, v] of Object.entries(s.values)) {
      if (v != null) fill.setValue(k, String(v));
    }
  }
  if (s.includes && typeof s.includes === 'object') {
    sectionIncludes.value = {
      directors: s.includes.directors !== false,
      secretary: s.includes.secretary !== false,
      shareholders: s.includes.shareholders !== false,
      controllers: s.includes.controllers !== false,
      engagement: s.includes.engagement !== false,
    };
  }
  if (s.signers) signersCtx.importAll(s.signers);
  if (s.local) {
    pendingLocalRestore.value = s.local;
    flushLocalRestore();
  }
}

const TASK_CODE = 'B32109';

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
/** ISO 날짜(YYYY-MM-DD) → "30 June" (회계연도 말일처럼 매년 반복되는 날짜용). ISO 가 아니면 '' */
function toDayMonth(v: unknown): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(v ?? '').trim());
  if (!m) return '';
  const month = MONTHS[Number(m[2]) - 1];
  return month ? `${Number(m[3])} ${month}` : '';
}

async function createTask() {
  if (creating.value) return;
  if (!effectiveTitle.value) {
    progress.value = 'Please enter a Task Title.';
    return;
  }
  creating.value = true;
  progress.value = editMode.value ? 'Updating task…' : 'Creating task…';
  try {
    const taskTypeCd = String(props.selection.task.TASK_CODE || TASK_CODE).trim() || TASK_CODE;
    await ensureCspLetterheadFilled();
    const valuesSnap = fill.exportPayload();
    signersCtx.seedFromValuesIfEmpty(valuesSnap);
    const baseFormRaw: Record<string, any> = { ...valuesSnap };
    // 인물 문서 스코프 값(f45_1/… 등)을 최상위로 끌어올리되, **문서 고유 일자**는 올리지 않는다.
    //   f45b_1/execution_day·month·year 가 최상위로 오면 normalize 가 dated_on 을
    //   "20 day of July, 2026" 으로 만들어 First Board·Incorporation Documents 의 빈 날짜칸에
    //   찍혔다(R-1).
    const NO_HOIST = new Set([
      'dated_on', 'execution_day', 'execution_month', 'execution_year', 'execution_date',
      'appointment_effective_date', 'consent_effective_date',
      // Form 45 증인(f45_N/witness_*)은 Letter of Indemnity 증인(최상위 witness_*)과 다른 사람
      'witness_name', 'witness_identification_no', 'witness_occupation', 'witness_date',
    ]);
    for (const [k, v] of Object.entries(valuesSnap)) {
      if (k.includes('/') && v) {
        const bare = bareKey(k);
        if (NO_HOIST.has(bare)) continue;
        if (!baseFormRaw[bare]) baseFormRaw[bare] = v;
      }
    }
    // CSP 키가 autofill 직후 export 에 빠져 있으면 한 번 더 반영
    if (!String(baseFormRaw.csp_name || '').trim() && fill.values.csp_name) {
      baseFormRaw.csp_name = fill.values.csp_name;
    }
    const signersMap = signersCtx.exportAll();
    // ACRA Risk Signature — Signers → Word 텍스트 변수
    const recSig = signersMap[SG_PRE_SIGNER_KEYS.riskRecommend]?.[0]?.name;
    const appSig = signersMap[SG_PRE_SIGNER_KEYS.riskApprove]?.[0]?.name;
    if (recSig) baseFormRaw.recommending_officer_signature = String(recSig).trim();
    if (appSig) baseFormRaw.approving_officer_signature = String(appSig).trim();
    const baseForm = normalizeSgPreFormDataForWord(baseFormRaw);
    const fallbackSigners = buildSgPreSignersFromValues(valuesSnap);

    const steps = (taskConfigSteps.value || []).map((s) => String(s).toLowerCase());
    const taskFlow = {
      createDocument: steps.length === 0 ? true : steps.includes('doc'),
      requestTSign: steps.includes('t-sign'),
      proceedWithEFiling: steps.includes('e-filing'),
    };
    const officerData = {
      entityCd: entityCd.value || null,
      icpCd: entityCd.value || null,
      presetForm: gatherState(),
      docType: 'SG_INCORPORATION_PRE',
      // 재진입(edit) 시 Aura 전용 패널로 복원되도록 My Preset 출처 마커 저장
      isMyPreset: props.selection?.isMyPreset === true,
      formData: baseForm,
      signers: signersMap,
    };

    let taskId: number | null = null;
    if (editMode.value) {
      taskId = Number(props.editTaskId) || null;
      if (!taskId) {
        progress.value = 'Error: invalid edit task id';
        creating.value = false;
        return;
      }
      await fetch(`/api/startTask/tasks/${taskId}/data`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ taskData: { formData: officerData } }),
      });
      await fetch(`/api/startTask/tasks/${taskId}/title`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ taskTitle: effectiveTitle.value }),
      });
    } else {
      const taskRes = await fetch('/api/startTask/tasks', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          entityCd: entityCd.value || null,
          taskTypeCd,
          taskTypeName: props.selection.task.TASK_NAME || 'Incorporation (Pre)',
          taskTitle: effectiveTitle.value,
          taskFlow,
          officerData,
        }),
      }).then((r) => r.json());
      taskId = Number(taskRes?.data?.taskId || taskRes?.taskId || 0);
      if (!taskId) {
        progress.value = `Error creating task: ${taskRes?.error?.message || taskRes?.message || 'no taskId'}`;
        creating.value = false;
        return;
      }
    }

    progress.value = 'Preparing documents…';
    // Edit 재진입 시 selection.documents 가 원본 템플릿이 아니라 **생성된 부수 문서**(같은 tmpId 다수,
    //   이름에 " — <사람/역할>" 접미사)일 수 있음 → tmpId 기준 dedupe 해야 인당 확장이 중복되지 않는다.
    const rawDocs = props.selection.documents || [];
    const seenTmpl = new Set<number>();
    const docs = rawDocs.filter((d: any) => {
      const tid = Number(d?.tmpId || d?.templateId || 0);
      if (!tid) return true;
      if (seenTmpl.has(tid)) return false;
      seenTmpl.add(tid);
      return true;
    });
    const local: any = docRef.value?.gatherLocalState?.() || pendingLocalRestore.value || {
      form45Ids: [1],
      form45aIds: [], // 법원 허가 특수 케이스 — 기본 미생성
      form45bIds: [1],
      corpCtrlIds: [1],
      indCtrlIds: [1],
      csProfileIds: [1],
      shareAppIds: [1],
      ddfIds: [1],
    };
    const companyLabel = String(baseForm.company_name || baseForm.entity_name || 'Company');

    type DocSave = {
      templateId: number;
      label: string;
      personName: string;
      entityCd?: string;
      formData: Record<string, any>;
      signers?: any[];
    };
    const docsToSave: DocSave[] = [];

    type SgPreKind =
      | 'form45'
      | 'form45a'
      | 'share_application'
      | 'ddf'
      | 'form45b'
      | 'notice_corp'
      | 'notice_ind'
      | 'nominee_director'
      | 'nominee_shareholder'
      | 'cs_profile'
      | 'engagement'
      | 'constitution'
      | 'client'
      | 'risk'
      | 'firstboard'
      | 'incdocs'
      | 'other';
    function classifySgPreDoc(name: string): SgPreKind {
      const n = String(name || '').toLowerCase();
      // Aura 고유 — Form 45A(법원 허가) 는 Form 45 보다 먼저 판정해야 한다.
      if (/form\s*45\s*a|form45a/.test(n)) return 'form45a';
      if (/share\s*application/.test(n)) return 'share_application';
      if (/\bddf\b|disclosure\s*of\s*interest/.test(n)) return 'ddf';
      if (/form\s*45\s*b|form45b/.test(n)) return 'form45b';
      if (/form\s*45|form45/.test(n)) return 'form45';
      // Notice for Nominee Director / Shareholder (s.386AL / 386ALB) — controller 판정보다 먼저
      if (/nominee/.test(n) && /director/.test(n)) return 'nominee_director';
      if (/nominee/.test(n) && /shareholder/.test(n)) return 'nominee_shareholder';
      if (/notice/.test(n) && /(corporate|corp)/.test(n)) return 'notice_corp';
      if (/notice/.test(n) && /(individual|ind)/.test(n)) return 'notice_ind';
      if (/notice/.test(n) && /controller/.test(n)) return 'notice_ind';
      if (/corporate\s*shareholder|shareholder\s*profile/.test(n)) return 'cs_profile';
      if (/engagement/.test(n)) return 'engagement';
      if (/constitution/.test(n)) return 'constitution';
      if (/client\s*acceptance/.test(n)) return 'client';
      if (/risk\s*assessment|acra\s*risk/.test(n)) return 'risk';
      // First Board 결의서 — Incorporation Documents 와 같은 [[Dated_On]] 을 쓰므로
      // 자체 일자를 갖도록 별도 종류로 분리한다(아래 DOC_OWN_DATE_KEY).
      if (/first\s*board|board\s*of\s*director/.test(n)) return 'firstboard';
      // Incorporation Documents 번들 — [[dated_on]] 이 다른 문서와 전역 공유되지 않도록
      //   자체 일자 키(incdocs_dated_on)를 갖는다.
      if (/incorporation\s*documents/.test(n)) return 'incdocs';
      return 'other';
    }

    /**
     * 문서마다 자기 일자를 갖게 하는 키.
     *
     * `[[Dated_On]]` 은 여러 문서가 **같은 이름**을 쓰기 때문에, 화면에서 문서별로
     * 다른 날짜를 입력해도 프리셋 전역 값 하나로 통일돼 버린다(실제로 First Board 에
     * 넣은 일자가 Incorporation Documents 값으로 덮였다).
     * → 문서 전용 키를 따로 두고, 생성 직전에 그 문서의 dated_on 으로 바꿔 준다.
     */
    const DOC_OWN_DATE_KEY: Partial<Record<SgPreKind, string>> = {
      firstboard: 'fbod_dated_on',
      client: 'declaration_date',
      engagement: 'acceptance_date',
      incdocs: 'incdocs_dated_on',
    };
    /**
     * 인물별로 여러 부 만들어지는 문서에서 **그 인물 것만** 써야 하는 키.
     *
     * 최상위(스코프 없는) `director_email` 같은 값은 normalize 의
     * flattenIndexedPersonRows 가 `dir1_director_email` 을 접어 만든 것이라,
     * 스코프에 값이 없으면 **1번 인물 값이 모든 부수에 찍힌다**(타인 개인정보 오출력).
     * → 스코프 문서를 만들 때는 해당 계열 최상위 키를 먼저 지우고, 스코프 값만 채운다.
     */
    const SCOPE_OWNED_KEYS: Array<{ re: RegExp; keys: string[] }> = [
      {
        re: /^f45_\d+\/$/,
        keys: [
          'director_name', 'director_identification_no', 'director_nationality',
          'director_address', 'director_alternate_address', 'director_residential_address',
          'director_contact_address',
          'director_email', 'director_mobile_no', 'director_tel_no', 'director_date_of_birth',
          'witness_name', 'witness_identification_no', 'witness_occupation', 'witness_date',
          'interpreter_language', 'interpreter_name', 'interpreter_nric',
          // ⚠ (c)(i)/(c)(ii) 통역·기타 조건부 플래그도 이사별로 독립. owned 에 없으면 한 이사에서 켠
          //   needs_interpreter 최상위 값이 다른 이사 문서에도 새어 (c)(i) 이 사라진다.
          'needs_interpreter', 'has_pre_1993_history', 'is_employment_pass_holder', 'uses_passport',
          'appointment_effective_date', 'consent_effective_date',
          'execution_day', 'execution_month', 'execution_year', 'dated_on',
        ],
      },
      {
        re: /^f45b_\d+\/$/,
        keys: [
          'secretary_name', 'secretary_identification_no', 'secretary_nationality',
          'secretary_address', 'secretary_residential_address', 'secretary_email',
          'appointment_effective_date', 'consent_effective_date', 'execution_date', 'dated_on',
        ],
      },
      {
        // Share Application — 개인주주 1인당 1부.
        //   이 키들의 **최상위 값은 다른 의미**라 그대로 두면 엉뚱한 값이 찍힌다:
        //     · number_of_shares  최상위 = 회사 총 발행주식수 (First BOD 용)
        //                         스코프 = 그 주주가 신청한 주식수
        //     · applicant_*       최상위 = 먼저 접힌 아무 주주 (flattenIndexedPersonRows)
        //   화면에서 그 칸을 비워 두면 조용히 최상위 값이 새어 들어온다.
        re: /^shapp_\d+\/$/,
        keys: [
          'applicant_name', 'applicant_identification_no', 'applicant_address',
          'applicant_signature',
          'applicant_identification_type', 'applicant_nationality', 'applicant_email', 'applicant_mobile_no',
          // 신버전 Share Application 확장 필드(주주별 격리) — normalize 가 applicant_* 에서 alias.
          'shareholder_name', 'shareholder_identification_no', 'shareholder_identification_type',
          'shareholder_nationality', 'shareholder_residential_address', 'shareholder_contact_address',
          'shareholder_email_address', 'shareholder_contact_number', 'shareholder_signature',
          'number_of_shares', 'type_of_shares', 'amount_payable', 'total_amount_payable', 'currency',
          'dated_on',
        ],
      },
      {
        // Notice for Controller (Corporate) — 법인 컨트롤러별. 최상위 값(다른 문서·접힌 1번 인물)이 새지 않게.
        re: /^ctrlc_\d+\/$/,
        keys: [
          'notice_date', 'dated_on', 'is_controller', 'knows_other_controller',
          'corporate_controller_name', 'corporate_controller_registration_no', 'controller_representative_name',
          'controller_entity_name', 'controller_entity_uen', 'controller_registered_office_address',
          'controller_entity_email', 'controller_entity_contact_no', 'controller_legal_form', 'controller_jurisdiction',
          'controller_corporate_register_name', 'controller_corporate_register_no', 'controller_date_became_corporate_controller',
          'other_controller_full_name', 'other_controller_alias', 'other_controller_residential_address',
          'other_controller_email', 'other_controller_contact_no', 'other_controller_nationality',
          'other_controller_identification_no', 'other_controller_date_of_birth',
          'other_controller_date_became_individual_controller', 'other_controller_entity_name', 'other_controller_entity_uen',
          'other_controller_registered_office_address', 'other_controller_legal_form', 'other_controller_entity_email',
          'other_controller_entity_contact_no', 'other_controller_jurisdiction', 'other_controller_corporate_register_name',
          'other_controller_corporate_register_no', 'other_controller_date_became_corporate_controller',
        ],
      },
      {
        // Notice for Controller (Individual) — 개인 컨트롤러별.
        //   생성 직전 hoist 가 법인 Notice 부수 값(ctrlc_1/controller_entity_name·_uen)을 최상위로 끌어올려,
        //   개인 Notice 의 (b) 법인란에 법인 컨트롤러명·UEN 이 찍혔다(고객 회신 2026-10-26).
        //   (b) 법인 키와 (a) 개인 키 모두 최상위 값을 막고 그 부수 값만 쓴다.
        re: /^ctrli_\d+\/$/,
        keys: [
          'controller_entity_name', 'controller_entity_uen', 'controller_registered_office_address',
          'controller_registered_office', 'controller_corporate_name', 'controller_uen',
          'controller_entity_email', 'controller_entity_contact_no', 'controller_legal_form', 'controller_jurisdiction',
          'controller_jurisdiction_statute', 'controller_corporate_register_name', 'controller_corporate_register_no',
          'controller_register_name', 'controller_registration_no', 'controller_date_became_corporate_controller',
          'corporate_controller_name', 'corporate_controller_registration_no',
          'controller_full_name', 'controller_name', 'controller_alias', 'controller_aliases',
          'controller_identification_no', 'controller_nationality', 'controller_residential_address',
          'controller_date_of_birth', 'controller_date_became_individual_controller',
          'controller_email', 'controller_contact_no', 'confirming_controller_name',
        ],
      },
      {
        // Notice for Nominee Director / Shareholder — 인물별 통지. 지명자(nominator)·일자는 그 부수 값만.
        re: /^(nd|ns)_\d+\/$/,
        keys: [
          'dated_on', 'is_nominee_director', 'is_nominee_shareholder', 'nominee_director_name', 'nominee_shareholder_name',
          'nominator_full_name', 'nominator_alias', 'nominator_residential_address', 'nominator_email', 'nominator_contact_no',
          'nominator_nationality', 'nominator_identification_no', 'nominator_date_of_birth', 'nominator_date_of_nomination',
          'nominator_entity_name', 'nominator_entity_uen', 'nominator_registered_office_address', 'nominator_entity_email',
          'nominator_entity_contact_no', 'nominator_legal_form', 'nominator_jurisdiction', 'nominator_corporate_register_name',
          'nominator_corporate_register_no', 'nominator_entity_date_of_nomination',
        ],
      },
    ];

    function formDataForScope(scopePrefix: string) {
      const out: Record<string, any> = {};
      const owned = SCOPE_OWNED_KEYS.find((r) => r.re.test(scopePrefix))?.keys;
      for (const [k, v] of Object.entries(baseForm)) {
        if (String(k).includes('/')) continue;
        if (owned && owned.includes(String(k))) continue; // 최상위 값 차단 — 스코프 값만 인정
        out[k] = v;
      }
      for (const [k, v] of Object.entries(baseForm)) {
        if (!String(k).startsWith(scopePrefix)) continue;
        const bare = String(k).slice(scopePrefix.length);
        if (bare) out[bare] = v;
        out[k] = v;
      }
      return normalizeSgPreFormDataForWord(out);
    }
    function signersForKind(kind: SgPreKind): any[] | undefined {
      const key =
        kind === 'engagement'
          ? SG_PRE_SIGNER_KEYS.engagement
          : kind === 'constitution'
            ? SG_PRE_SIGNER_KEYS.constitution
            : kind === 'client'
              ? SG_PRE_SIGNER_KEYS.client
              : '';
      if (!key) return undefined;
      const list = signersMap[key];
      if (list?.length) return list;
      return fallbackSigners.length ? fallbackSigners : undefined;
    }

    /**
     * 1인당 1부 문서의 **자동 서명자 배정**.
     *   Form 45(이사) / 45A(이사) / 45B(비서) / Notice(비서) / Share Application(신청 주주) /
     *   DDF(이사) 는 서명 주체가 문서마다 정해져 있으므로, 해당 인물을 그대로 서명자로 넣는다.
     *   (넣지 않으면 문서 목록에 "Signer assignment required" 로 남아 T-Sign 을 못 보낸다)
     */
    /**
     * 여러 서명 주체가 있는 문서용 — 이름이 있는 사람만 순서대로 넣는다.
     *   예) Form 45 = 취임 동의하는 **이사** + 그 서명을 목격한 **증인**(제3자).
     *       증인은 자기 서명을 자기가 목격할 수 없으므로 반드시 별개 인물이다.
     */
    function autoSigners(
      entries: Array<{ tag: string; id: number | string; name: unknown; title: string; email?: unknown }>,
    ): any[] | undefined {
      const out: any[] = [];
      for (const e of entries) {
        const s = autoSigner(e.tag, e.id, e.name, e.title, e.email);
        if (s) out.push(s[0]);
      }
      return out.length ? out : undefined;
    }

    function autoSigner(
      tag: string,
      id: number | string,
      name: unknown,
      title: string,
      email?: unknown,
    ): any[] | undefined {
      const nm = String(name ?? '').trim();
      if (!nm) return undefined;
      return [
        {
          id: `aura-${tag}-${id}`,
          name: nm,
          title,
          role: /secretary/i.test(title) ? 'secretary' : 'director',
          email: String(email ?? '').trim(),
          isManual: true,
          showTitle: true,
          _signerLayout: 'box',
        },
      ];
    }
    function pushOnce(
      d: any,
      formData: Record<string, any>,
      personName: string,
      labelSuffix = '',
      signers?: any[],
    ) {
      const tid = Number(d?.tmpId || d?.templateId || 0);
      if (!tid) return;
      // 생성 파일명에 이미 붙은 접미사(" — <사람/역할>")를 제거해 재저장 시 누적되지 않게 한다.
      //   원본 템플릿명은 " - "(hyphen)만 쓰므로 em dash(" — ") 앞부분만 취하면 안전.
      const baseLabel = String(d?.templateName || d?.docCode || 'Document').replace(/\.docx?$/i, '').split(' — ')[0].trim();
      docsToSave.push({
        templateId: tid,
        label: labelSuffix ? `${baseLabel} — ${labelSuffix}` : baseLabel,
        personName,
        // Pre 단계 icpCd 는 L_ENTITY 가 아님 — prepare-documents 에 entityCd 로 넣지 않음
        formData: normalizeSgPreFormDataForWord(formData),
        signers: signers?.length ? signers : undefined,
      });
    }

    const incl = sectionIncludes.value;
    for (const d of docs) {
      const kind = classifySgPreDoc((d as any).templateName || (d as any).docCode || '');
      if (kind === 'form45') {
        if (!incl.directors) continue;
        const ids = local.form45Ids?.length ? local.form45Ids : [];
        for (const id of ids) {
          const scope = `f45_${id}/`;
          const fd = formDataForScope(scope);
          const person = String(fd.director_name || `Director ${id}`);
          // 서명 주체 2인 — 취임 동의하는 이사 + 그 서명을 목격한 증인(별개 인물).
          //   이사 서명자는 화면 Signers 편집(store)을 우선 사용하고, 없으면 director_name 으로 자동 배정.
          const dirSigners = signersMap[`f45_${id}/director_Signers`]?.length
            ? signersMap[`f45_${id}/director_Signers`]
            : autoSigner('f45', id, fd.director_name, 'Director', fd.director_email) || [];
          const witnessSigners = autoSigner('f45w', id, fd.witness_name, 'Witness') || [];
          const f45Signers = [...dirSigners, ...witnessSigners];
          pushOnce(d, fd, person, person, f45Signers.length ? f45Signers : undefined);
        }
        continue;
      }
      if (kind === 'form45b') {
        // VL 고정본 — 비서 포함 여부와 무관하게 화면에 있는 부수만큼 생성(Aura Incorporation 은 기본 1부)
        const ids = local.form45bIds?.length ? local.form45bIds : [];
        for (const id of ids) {
          const scope = `f45b_${id}/`;
          const fd = formDataForScope(scope);
          // "Form 45B - VL" 템플릿 토큰 — 화면은 Incorporation 기존 키를 쓴다(AuraForm45bVlDoc)
          if (!String(fd.company_uen || '').trim()) fd.company_uen = fd.company_registration_no || '';
          if (!String(fd.effective_date || '').trim()) fd.effective_date = fd.appointment_effective_date || fd.consent_effective_date || '';
          const person = String(fd.secretary_name || `Secretary ${id}`);
          // VL 본은 비서 서명이 템플릿에 이미지로 고정 → T-Sign 서명자 없음(On Board Form 45B 와 동일)
          pushOnce(d, fd, person, person);
        }
        continue;
      }
      if (kind === 'notice_corp') {
        if (!incl.controllers) continue;
        const ids = local.corpCtrlIds?.length ? local.corpCtrlIds : [];
        for (const id of ids) {
          const scope = `ctrlc_${id}/`;
          const fd = formDataForScope(scope);
          const person = String(fd.corporate_controller_name || fd.controller_corporate_name || `Corporate Controller ${id}`);
          // Aura Corporate Notice: 발신 비서(FLORENSIA)는 원문 고정 글자이고, 서명 칸([[controller_signature]])은
          //   ACKNOWLEDGEMENT 의 법인 컨트롤러 대표자 — 대표자를 서명자로.
          pushOnce(d, fd, person, person, autoSigner('ctrlc', id, fd.controller_representative_name, 'Authorised Representative'));
        }
        continue;
      }
      if (kind === 'nominee_director' || kind === 'nominee_shareholder') {
        // 이사/주주 1인당 1부 — 통지를 받는 본인이 선언·서명 (No 여도 본인이 서명, 문서엔 N.A.)
        const isDir = kind === 'nominee_director';
        const ids: number[] = ((local as any)[isDir ? 'nomDirIds' : 'nomShIds'] || []) as number[];
        for (const id of ids) {
          const fd = formDataForScope(`${isDir ? 'nd' : 'ns'}_${id}/`);
          const nm = isDir ? fd.nominee_director_name : fd.nominee_shareholder_name;
          const person = String(nm || `${isDir ? 'Director' : 'Shareholder'} ${id}`);
          pushOnce(d, fd, person, person, autoSigner(isDir ? 'nd' : 'ns', id, nm, isDir ? 'Director' : 'Shareholder'));
        }
        continue;
      }
      if (kind === 'notice_ind') {
        if (!incl.controllers) continue;
        const ids = local.indCtrlIds?.length ? local.indCtrlIds : [];
        for (const id of ids) {
          const scope = `ctrli_${id}/`;
          const fd = formDataForScope(scope);
          const person = String(fd.controller_full_name || `Individual Controller ${id}`);
          pushOnce(d, fd, person, person, autoSigner('ctrli', id, fd.secretary_name, 'Company Secretary'));
        }
        continue;
      }
      if (kind === 'cs_profile') {
        if (!incl.shareholders) continue;
        const ids = local.csProfileIds?.length ? local.csProfileIds : [];
        for (const id of ids) {
          const scope = `cs_${id}/`;
          const fd = formDataForScope(scope);
          const person = String(fd.cs_company_name || `Corporate Shareholder ${id}`);
          pushOnce(d, fd, person, person);
        }
        continue;
      }
      // ── Aura 고유: 1인당 1부 문서 ──
      if (kind === 'form45a') {
        // 법원 허가 특수 케이스 — 추가한 부수만 생성(없으면 문서 자체를 만들지 않음)
        const ids = (local as any).form45aIds?.length ? (local as any).form45aIds : [];
        for (const id of ids) {
          const fd = formDataForScope(`f45a_${id}/`);
          const person = String(fd.director_name || `Director ${id}`);
          // 서명 주체 = 법원 허가를 받아 취임하는 그 이사
          pushOnce(d, fd, person, person, autoSigner('f45a', id, fd.director_name, 'Director', fd.director_email));
        }
        continue;
      }
      if (kind === 'share_application') {
        // 주주 1인당 1부 — 개인 주주(CAF sh 행) + 법인 주주(cs_{N}/ → id CORP_SHAPP_BASE+N)
        const ids = (local as any).shareAppIds?.length ? (local as any).shareAppIds : [1];
        for (const id of ids) {
          const fd = formDataForScope(`shapp_${id}/`);
          const name = String(fd.shareholder_name || fd.applicant_name || '').trim();
          const person = name || `Applicant ${id}`;
          // 서명 주체 = 주식을 신청한 그 주주. 법인 주주는 프로필의 담당자(Contact Person)가 서명.
          const isCorp = id > CORP_SHAPP_BASE;
          const signerName = isCorp ? String(baseForm[`cs_${id - CORP_SHAPP_BASE}/cs_contact_person`] || '').trim() : name;
          pushOnce(d, fd, person, person, autoSigner(
            'shapp', id, signerName,
            isCorp ? 'Authorised Representative' : 'Applicant',
            isCorp ? baseForm[`cs_${id - CORP_SHAPP_BASE}/cs_email`] : fd.shareholder_email_address,
          ));
        }
        continue;
      }
      if (kind === 'ddf') {
        // 이사 1인당 1부 (Companies Act s.156/165 개인 선언서)
        const ids = (local as any).ddfIds?.length ? (local as any).ddfIds : [1];
        for (const id of ids) {
          const fd = formDataForScope(`ddf_${id}/`);
          const person = String(fd.director_name || `Director ${id}`);
          // 서명 주체 = 이해관계를 신고하는 그 이사 본인
          pushOnce(d, fd, person, person, autoSigner('ddf', id, fd.director_name, 'Director', fd.director_email));
        }
        continue;
      }
      if (kind === 'engagement' && !incl.engagement) continue;
      // 회사 단위 문서(First BOD / Incorporation Documents / Constitution 등)
      //   — 지정된 서명자가 없으면 이사 전원(fallbackSigners)을 자동 배정
      const companyDocSigners = signersForKind(kind)
        || (fallbackSigners.length ? fallbackSigners : undefined);
      // 회사 단위 문서에는 **인물별 스코프 키를 싣지 않는다**.
      //   baseForm 에는 f45_1/… · f45b_1/… · shapp_2/… · ddf_1/… · cs_1/… 처럼
      //   1인당 1부 문서 전용 키가 전부 들어 있는데, 회사 문서 템플릿은 이 이름을
      //   하나도 쓰지 않는다(Aura 템플릿 16종 · XML 155개 확인 — 슬래시 변수 0개).
      //   그런데도 백엔드 치환기는 변수 하나마다 XML 전체를 훑기 때문에, 안 쓰는 키가
      //   그대로 시간이 된다. 인원이 늘수록 이 낭비가 비례해서 커진다.
      //   (인물 문서는 formDataForScope 가 이미 자기 스코프만 남긴다)
      const companyFd: Record<string, any> = {};
      for (const [k, v] of Object.entries(baseForm)) {
        if (String(k).includes('/')) continue;
        companyFd[k] = v;
      }
      const ownDateKey = DOC_OWN_DATE_KEY[kind];
      if (ownDateKey) {
        // 자기 일자가 비어 있으면 dated_on 을 **지운다**(빈칸 유지). normalize 가 execution_* 로
        //   dated_on 을 다시 만들지 않도록 그 키들도 뺀다 — 남의 문서 날짜가 찍히는 것 방지(R-1).
        for (const k of ['execution_day', 'execution_month', 'execution_year', 'execution_date']) {
          delete companyFd[k];
        }
        if (String(companyFd[ownDateKey] ?? '').trim()) companyFd.dated_on = companyFd[ownDateKey];
        else delete companyFd.dated_on;
      }
      // First Board / Incorporation Documents: "financial year shall end on [[financial_year_end]]
      //   of each year" — 연도 없이 "30 June" 으로 (날짜칸이라 30/06/2027 로 찍혔다, N-8).
      if (kind === 'firstboard' || kind === 'incdocs') {
        const fyeDM = toDayMonth(companyFd.financial_year_end);
        if (fyeDM) companyFd.financial_year_end = fyeDM;
      }
      if (kind === 'risk') {
        // ACRA 서명칸 2개는 서명자 피커 키 — 백엔드가 **서명자 JSON 배열**을 받아야 서명란을 그린다.
        //   (예전엔 담당자 이름 문자열만 넘어가 칸이 비었고, T-Sign 서명자는 기본값인 이사 전원으로 저장됐다)
        //   Recommending / Approving Officer 서명자(화면 Signers) → 각 칸, T-Sign 서명자 = 두 담당자.
        const rec: any[] = signersMap[SG_PRE_SIGNER_KEYS.riskRecommend] || [];
        const app: any[] = signersMap[SG_PRE_SIGNER_KEYS.riskApprove] || [];
        if (rec.length) companyFd.recommending_officer_signature = JSON.stringify(rec);
        else delete companyFd.recommending_officer_signature;
        if (app.length) companyFd.approving_officer_signature = JSON.stringify(app);
        else delete companyFd.approving_officer_signature;
        const sameName = (a: any, b: any) => String(a?.name || '').trim().toUpperCase() === String(b?.name || '').trim().toUpperCase();
        const officers = [...rec, ...app.filter((a) => !rec.some((r) => sameName(r, a)))];
        pushOnce(d, companyFd, companyLabel, '', officers.length ? officers : undefined);
        continue;
      }
      pushOnce(d, companyFd, companyLabel, '', companyDocSigners);
    }

    if (docsToSave.length) {
      try {
        const saveRes = await fetch('/api/aiDocuments/incorporation/prepare-documents', {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            taskId,
            documents: docsToSave,
            replaceExisting: editMode.value,
          }),
        }).then((r) => r.json());
        if (!saveRes?.success) {
          progress.value = `⚠ Documents: ${saveRes?.message || 'unknown'}`;
          console.warn('[SgIncorporationPre] prepare-documents failed:', saveRes);
        }
      } catch (err: any) {
        console.warn('[SgIncorporationPre] document save failed:', err);
        progress.value = `⚠ Document save failed: ${err?.message || err}`;
      }
    }

    try {
      await fetch(`/api/startTask/tasks/${taskId}/document-status`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });
    } catch {
      /* ignore */
    }
    try {
      await fetch('/api/startTask/preset/use-log', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          presetTaskId: props.selection.task.PRESET_TASK_ID,
          taskId,
        }),
      });
    } catch {
      /* ignore */
    }

    emit('task-created', { taskId: taskId!, taskTitle: effectiveTitle.value });
    router.push({ name: 'PreSetTaskDetail', query: { taskId: String(taskId) } });
  } catch (e: any) {
    console.error('[SgIncorporationPre] create failed', e);
    progress.value = e?.message || 'Create failed';
  } finally {
    creating.value = false;
  }
}

defineExpose({ gatherState, restoreState, createTask });
</script>

<template>
  <div class="sg-inc-pre-panel" :class="{ studio }">
    <div v-if="!studio" class="co-head">
      <div>
        <div class="co-title">{{ selection.task.TASK_NAME || 'Incorporation (Pre)' }}</div>
        <div class="co-subtitle">SG Incorporation (Pre) — live document package</div>
      </div>
      <div class="co-actions">
        <v-btn variant="text" size="small" class="text-none" @click="emit('change-task-title')">Change title</v-btn>
        <v-btn variant="text" size="small" class="text-none" @click="emit('change-entity')">Change entity</v-btn>
      </div>
    </div>

    <div v-if="!studio" class="co-title-row">
      <v-text-field
        v-model="titleModel"
        density="compact"
        variant="outlined"
        hide-details
        label="Task Title"
        placeholder="e.g. SG Incorporation (Pre) — NewCo Pte. Ltd."
      />
    </div>

    <div v-if="!studio && entityCd" class="co-entity">
      Linked incorporation: <strong>{{ entityData?.entityName || entityData?.companyName || entityCd }}</strong>
    </div>

    <div class="co-doc">
      <AuraIncorporationPackageDoc
        ref="docRef"
        :includes="sectionIncludes"
        @sync-includes="onSyncIncludes"
      />
    </div>

    <div v-if="!studio" class="co-foot">
      <v-btn
        color="primary"
        class="text-none"
        :loading="creating"
        :disabled="!effectiveTitle || creating"
        @click="createTask"
      >
        {{ editMode ? 'Update task' : 'Create Task' }}
      </v-btn>
      <span v-if="progress && !creating" class="co-err">{{ progress }}</span>
    </div>
    <div v-else-if="progress && !creating" class="co-foot">
      <span class="co-err">{{ progress }}</span>
    </div>
  </div>
</template>

<style scoped>
.sg-inc-pre-panel { display: flex; flex-direction: column; gap: 12px; min-height: 0; }
.sg-inc-pre-panel.studio .co-foot { position: sticky; bottom: 0; background: #fff; padding: 10px 0; z-index: 2; }
.co-head { display: flex; justify-content: space-between; align-items: flex-start; gap: 12px; }
.co-title { font-size: 16px; font-weight: 800; color: #1a2336; }
.co-subtitle { font-size: 12px; color: #6b7a94; margin-top: 2px; }
.co-actions { display: flex; gap: 4px; flex-shrink: 0; }
.co-title-row { max-width: 520px; }
.co-entity { font-size: 12.5px; color: #42506a; }
.co-doc { flex: 1; min-height: 0; }
.co-foot { display: flex; align-items: center; gap: 12px; }
.co-err { font-size: 12px; color: #c0392b; }
</style>
